<template>
  <transition v-if="currentFrame" :name="transitionName">
    <Panel
      v-bind="panelForwardProps"
      :key="currentFrame.key"
      :items="renderedItems"
      :search="search"
      class="slide"
      @update-search="handleUpdateSearch"
      @update-status="emit('update-status', $event)"
      @update-selected="handleUpdateSelected"
      @select-item="handleSelectItem"
      @search-enter="handleSearchEnter"
      @exit-start="emit('exit-start')"
      @next="handleNext"
    >
      <template #no-data>
        <slot name="no-data" />
      </template>
    </Panel>
  </transition>
</template>

<script setup>
/**
 * Navigation container selected by `navigation="drilldown"`. Delegates the
 * frame-stack state machine to `useFrameStack`, mirrors search via
 * `useContainerSearch`, and feeds the active frame through
 * `useQuantumSelectItems`. The host wraps this container in
 * NavigationContainerShell and drives the navbar title from its own props.
 */
import { computed, toRef } from "vue";
import { forwardablePanelProps, panelProps, resultMappingProps } from "#ce/components/SpQuantumSelect/props.js";
import { useSpList } from "#ce/components/SpList/composables/useSpList";
import { booleanAttribute, toBoolean } from "#ce/utils/props";
import { useContainerSearch } from "#ce/components/SpQuantumSelect/composables/useContainerSearch.js";
import { useFrameStack } from "#ce/components/SpQuantumSelect/composables/useFrameStack.js";
import { useQuantumSelectItems } from "#ce/components/SpQuantumSelect/composables/useQuantumSelectItems";
import { useRenderedResults } from "#ce/components/SpQuantumSelect/composables/useRenderedResults";
import Panel from "./QuantumSelectPanel.ce.vue";

defineOptions({ inheritAttrs: false });

const emit = defineEmits([
  "update-status",
  "update-matches",
  "update-selected",
  "update-search",
  "update-current-item",
  "update:on-back",
  "select-item",
  "search-enter",
  "exit-start",
]);

const props = defineProps({
  ...panelProps,
  ...resultMappingProps,

  // Disables client-side filtering.
  noFilter: booleanAttribute(),
  loadChildren: {
    type: Function,
    required: false,
    default: undefined,
  },
  // Ancestor items to pre-drill into when the container mounts, top-most first.
  initialPath: {
    type: Array,
    default: () => [],
  },
});

const noFilter = computed(() => toBoolean(props.noFilter));

const { getItemTitle } = useSpList(props);

const { search, applySearchInput } = useContainerSearch(props, emit);

function handleSearchEnter(event) {
  emit("search-enter", event);
}

function handleUpdateSearch(value) {
  applySearchInput(value);
}

function handleUpdateSelected(value) {
  emit("update-selected", value);
}

function handleSelectItem(item) {
  emit("select-item", item);
}

const { currentFrame, currentItems, transitionName, pushFrame } = useFrameStack(props, emit, { noFilter });

function handleNext(item) {
  pushFrame(item);
}

const { groupedItems, hasMatches } = useQuantumSelectItems({
  items: currentItems,
  search,
  noFilter,
  getItemTitle,
  maxResults: toRef(props, "maxResults"),
  fuzzySearch: computed(() => toBoolean(props.fuzzySearch)),
  highlightFirstResult: computed(() => toBoolean(props.highlightFirstResult)),
  highlightIdleFirstResult: computed(() => toBoolean(props.highlightIdleFirstResult)),
  selectableBranches: computed(() => toBoolean(props.selectableBranches)),
  sectionOrder: toRef(props, "sectionOrder"),
  noSubstringFallback: computed(() => toBoolean(props.noSubstringFallback)),
});

const { renderedItems } = useRenderedResults(props, emit, groupedItems, hasMatches);

const panelForwardProps = computed(() => forwardablePanelProps(props));
</script>

<style scoped lang="scss">
.slide {
  min-width: 100%;
}

.slide-content-pop-leave-to,
.slide-content-push-enter-from {
  transform: translateX(100%);
}

.slide-content-pop-enter-to,
.slide-content-push-enter-to {
  transform: translateX(0%);
}

.slide-content-pop-enter-from,
.slide-content-push-leave-to {
  transform: translateX(-100%);
}

// `.slide` raises specificity above Panel's own `.sp-quantum-select-panel { position: relative }`.
// Both live in SpQuantumSelect's single shadow root, so without it the two rules tie and Panel's
// `relative` wins by source order, leaving both panels in flow instead of overlapping for the cross-slide.
.slide.slide-content-pop-enter-active,
.slide.slide-content-pop-leave-active,
.slide.slide-content-push-enter-active,
.slide.slide-content-push-leave-active {
  transition: var(
    --sp-comp-quantum-select-drilldown-container-transition,
    all var(--quantum-select-animation-time, 0.3s) ease-in-out
  );
  position: absolute;
}
</style>
